import React, { ReactNode, ReactElement, useState, useEffect, useRef } from 'react'
import { Text, Flex, ThemeUIStyleObject, Box, IconButton } from 'theme-ui'
import { Svg } from 'ui'
import ArrowUp from 'images/ui/context-menu-arrow-up.svg'

interface FeaturesInstructionsBoxProps {
  title?: ReactNode
  children: ReactNode
  sxTitle?: ThemeUIStyleObject
  sxContainer?: ThemeUIStyleObject
}

export default function FeaturesInstructionsBox({
  children,
  sxContainer,
}: {
  children: ReactNode
  sxContainer?: ThemeUIStyleObject
}): ReactElement {
  return (
    // should be Box instead of Flex to avoid overriding display: none
    <Box
      sx={{
        display: 'flex',
        borderRadius: '30px',
        flexDirection: 'column',
        width: '100%',
        p: ['1.6rem', '4.8rem'],
        backgroundColor: 'white',
        boxShadow: ({ colors }): string => `0 1.6rem 2.4rem 0 ${colors?.darkItemBoxShadow}`,
        justifyContent: 'center',
        textAlign: 'center',
        gap: '4.8rem',
        ...sxContainer,
      }}
    >
      {children}
    </Box>
  )
}

export function FeaturesInstructionsBoxSection({
  title,
  children,
  sxTitle,
}: FeaturesInstructionsBoxProps): ReactElement {
  const [areStepsOpened, setAreStepsOpened] = useState(false)
  const boxRef = useRef<HTMLDivElement | null>(null)
  const didMountRef = useRef(false)

  useEffect(() => {
    if (didMountRef.current && !areStepsOpened) {
      return boxRef.current?.scrollIntoView({ block: 'center' })
    }
    didMountRef.current = true
  }, [areStepsOpened])

  return (
    <Flex
      ref={boxRef}
      sx={{
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      {title && (
        <Text
          sx={{
            display: 'flex',
            fontSize: ['2.8rem', '4rem', '4.8rem'],
            fontWeight: 'bold',
            pb: ['1.2rem', '2.4rem', '4.8rem'],
            color: 'black',
            alignSelf: 'center',
            ...sxTitle,
          }}
        >
          {title}
        </Text>
      )}
      <Flex
        sx={{ display: ['flex', 'none'], justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Text sx={{ color: 'black', fontWeight: 'bold' }}>
          {areStepsOpened ? 'Close Steps' : 'View Steps'}
        </Text>
        <IconButton
          sx={{
            width: '4rem',
            height: '4rem',
            backgroundColor: 'black',
            borderRadius: '50px',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => {
            setAreStepsOpened(!areStepsOpened)
          }}
        >
          <Svg
            svg={ArrowUp}
            descriptiveText={areStepsOpened ? 'Hide steps' : 'Show steps'}
            fill="white"
            sx={{
              width: '2.4rem',
              height: '2.4rem',
              transform: `rotate(${areStepsOpened ? 0 : '180deg'})`,
            }}
          />
        </IconButton>
      </Flex>
      <Flex
        sx={{
          fontSize: '2.1rem',
          fontWeight: 'normal',
          color: 'white50',
          gap: ['1.2rem', '9.2rem', '1.8rem'],
          textAlign: 'center',
          justifyContent: 'center',
          flexDirection: ['column', 'row'],
          flexWrap: ['nowrap', 'wrap'],
          height: [areStepsOpened ? '100%' : 0, '100%'],
          overflow: 'hidden',
        }}
      >
        {children}
      </Flex>
      {areStepsOpened && (
        <Flex
          sx={{ display: ['flex', 'none'], justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Text sx={{ color: 'black', fontWeight: 'bold' }}>
            {areStepsOpened ? 'Close Steps' : 'View Steps'}
          </Text>
          <IconButton
            sx={{
              width: '4rem',
              height: '4rem',
              backgroundColor: 'black',
              borderRadius: '50px',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              setAreStepsOpened(!areStepsOpened)
            }}
          >
            <Svg
              svg={ArrowUp}
              descriptiveText={areStepsOpened ? 'Hide steps' : 'Show steps'}
              fill="white"
              sx={{
                width: '2.4rem',
                height: '2.4rem',
                transform: `rotate(${areStepsOpened ? 0 : '180deg'})`,
              }}
            />
          </IconButton>
        </Flex>
      )}
    </Flex>
  )
}

interface FeaturesInstructionProps {
  step: number
  Illustration: React.ElementType
  instructionText: string
}

export function FeaturesInstruction({
  step,
  Illustration,
  instructionText,
}: FeaturesInstructionProps): ReactElement {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: ['100%', '30%'],
        alignItems: 'center',
        mb: ['1.6rem', 0],
      }}
    >
      <Flex sx={{ alignItems: 'center', mb: ['1.6rem', '2.4rem'] }}>
        <Text
          sx={{
            border: '1px solid',
            borderColor: 'greenApple',
            borderRadius: '100%',
            backgroundColor: 'greenApple',
            width: '3rem',
            height: '3rem',
            color: 'white',
            fontWeight: 'bold',
            mr: '1.2rem',
            flexShrink: 0,
          }}
        >
          {step}
        </Text>
        <Svg
          svg={Illustration}
          descriptiveText={`Step ${step} illustration: ${instructionText}`}
          sx={{ height: '16rem' }}
        />
      </Flex>
      <Text
        sx={{
          color: 'black70',
          fontSize: ['1.8rem', '2.1rem', '2.1rem'],
          maxWidth: ['25.4rem', '25.4rem', '26.3rem'],
        }}
      >
        {instructionText}
      </Text>
    </Flex>
  )
}
